// Financeiro › Cadastros › Avisos — quem recebe cada aviso automático do
// financeiro. A lista desta tela É quem recebe (financeiro.aviso_destinatarios):
//   faturamento (e-mail "NF a emitir"), vencimento_cp (sino de contas vencendo),
//   reembolso_revisao (sino + e-mail) e inter_sync (e-mail quando o robô do
//   extrato do Banco Inter para, e quando volta). O catálogo (nome/descrição/
//   canais) vem do backend (dre-engine/handlers/avisosDestinatarios.js) — uma
//   fonte só, então aviso novo aparece aqui sem mexer nesta tela.
// Lista vazia = ninguém recebe; a tela pede confirmação antes de salvar assim.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Alert, Button, Card, Select, Space, Spin, Tag, Typography, App as AntApp,
} from 'antd';
import { API_URL } from '../config';
import { usePerms } from '../hooks/usePerms';

const { Text, Title } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface TipoAviso {
  tipo: string;
  nome: string;
  descricao: string;
  canais: string[];
  emails: string[];
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const CANAL_LABEL: Record<string, string> = {
  email: '📧 E-mail',
  sino: '🔔 Sino do sistema',
  telegram: '💬 Telegram (quem pareou)',
};

function FinanceiroAvisosInner() {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode('financeiro.avisos.editar') || pode('admin.permissoes.editar');

  const [tipos, setTipos] = useState<TipoAviso[]>([]);
  const [tabelaAusente, setTabelaAusente] = useState(false);
  const [loading, setLoading] = useState(true);
  // Edição por tipo: o que está no Select agora (a lista salva fica em tipos[]).
  const [edicao, setEdicao] = useState<Record<string, string[]>>({});
  const [salvando, setSalvando] = useState<string | null>(null);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/financeiro/avisos-destinatarios`,
        { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setTipos(data.tipos || []);
      setTabelaAusente(!!data.tabela_ausente);
      const ed: Record<string, string[]> = {};
      for (const t of (data.tipos || [])) ed[t.tipo] = t.emails;
      setEdicao(ed);
    } catch (e: any) {
      message.error('Erro ao carregar: ' + (e?.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }

  // Sugestões do dropdown: todo e-mail que já aparece em alguma lista.
  const sugestoes = useMemo(() => {
    const s = new Set<string>();
    for (const t of tipos) for (const e of t.emails) s.add(e);
    for (const lista of Object.values(edicao)) for (const e of lista) s.add(e);
    return Array.from(s).sort().map(v => ({ value: v }));
  }, [tipos, edicao]);

  function dirty(t: TipoAviso): boolean {
    const atual = edicao[t.tipo] || [];
    return atual.length !== t.emails.length || atual.some(e => !t.emails.includes(e));
  }

  async function salvar(t: TipoAviso) {
    const emails = (edicao[t.tipo] || []).map(e => e.trim().toLowerCase()).filter(Boolean);
    const invalido = emails.find(e => !EMAIL_RE.test(e));
    if (invalido) {
      message.error(`"${invalido}" não parece um e-mail. Corrija antes de salvar.`);
      return;
    }
    if (!emails.length) {
      const okVazio = await new Promise<boolean>(resolve => {
        modal.confirm({
          title: 'Deixar a lista vazia?',
          content: `Ninguém vai receber o aviso "${t.nome}" até alguém ser cadastrado de novo.`,
          okText: 'Salvar vazia',
          okButtonProps: { danger: true },
          cancelText: 'Voltar',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!okVazio) return;
    }
    setSalvando(t.tipo);
    try {
      const token = await getToken();
      const res = await axios.put(`${API_URL}/financeiro/avisos-destinatarios/${t.tipo}`,
        { emails }, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      const salvos: string[] = data.emails ?? emails;
      // Atualiza SÓ o card salvo — recarregar tudo descartaria em silêncio o
      // que a pessoa já digitou nos outros cards e ainda não salvou.
      setTipos(prev => prev.map(x => (x.tipo === t.tipo ? { ...x, emails: salvos } : x)));
      setEdicao(prev => ({ ...prev, [t.tipo]: salvos }));
      message.success(`Lista de "${t.nome}" salva.`);
    } catch (e: any) {
      message.error('Erro ao salvar: ' + (e?.response?.data?.error || e.message));
    } finally { setSalvando(null); }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  return (
    <div>
      <Title level={5} style={{ marginTop: 0 }}>Avisos automáticos — quem recebe</Title>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
        A lista de cada aviso é exatamente quem recebe. Adicione ou remova e-mails
        e clique em Salvar — vale na próxima vez que o aviso disparar.
      </Text>
      {tabelaAusente && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="O cadastro ainda não foi criado no banco (migration pendente). Os avisos seguem com as listas antigas até isso rodar — a edição fica travada até lá." />
      )}
      {!podeEditar && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Você pode ver as listas, mas não alterá-las (permissão de edição de Avisos)." />
      )}
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {tipos.map(t => (
          <Card key={t.tipo} size="small"
            title={<Space wrap size={6}>
              <span>{t.nome}</span>
              {t.canais.map(c => <Tag key={c} style={{ marginInlineEnd: 0 }}>{CANAL_LABEL[c] || c}</Tag>)}
            </Space>}
            extra={podeEditar && !tabelaAusente && (
              <Button type="primary" size="small" loading={salvando === t.tipo}
                disabled={!dirty(t)} onClick={() => salvar(t)}>
                Salvar
              </Button>
            )}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              {t.descricao}
            </Text>
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Adicionar e-mail…"
              value={edicao[t.tipo] || []}
              onChange={(v) => setEdicao(prev => ({ ...prev, [t.tipo]: v as string[] }))}
              options={sugestoes}
              tokenSeparators={[',', ';', ' ']}
              disabled={!podeEditar || tabelaAusente || salvando === t.tipo}
            />
            {!((edicao[t.tipo] || []).length) && (
              <Text type="danger" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                Lista vazia — ninguém recebe este aviso.
              </Text>
            )}
          </Card>
        ))}
      </Space>
    </div>
  );
}

export default function FinanceiroAvisos() {
  return <AntApp><FinanceiroAvisosInner /></AntApp>;
}
